/**
 * Leviathan Web - Credit Management Dashboard
 * Quasar Prime LLC
 * Aggressive Tactical Industrial Design
 *
 * Credit Management - AR dashboard with holds, at-risk customers, and credit overview
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  DollarSign,
  AlertTriangle,
  Users,
  TrendingUp,
  CreditCard,
  ShieldAlert,
  ShieldCheck,
  Clock,
  Search,
  RefreshCw,
  ChevronRight,
  XCircle,
  CheckCircle,
} from 'lucide-react';
import { API_URL } from '@/config/api';

// Types
interface CreditOverview {
  totalCustomers: number;
  customersOnHold: number;
  customersWarning: number;
  customersWithCredit: number;
  totalAR: number;
  totalCreditExtended: number;
  recentHolds: Array<{
    id: string;
    reason: string;
    holdDate: string;
    holdAmount: number | null;
    isActive: boolean;
    customer: {
      id: string;
      customerNumber: string;
      name: string;
    };
  }>;
}

interface CustomerOnHold {
  id: string;
  reason: string;
  holdDate: string;
  holdAmount: number | null;
  notes: string | null;
  isActive: boolean;
  customer: {
    id: string;
    customerNumber: string;
    name: string;
    currentBalance: number;
    creditLimit: number | null;
    creditStatus: string;
  };
}

interface CustomerAtRisk {
  id: string;
  customerNumber: string;
  name: string;
  creditLimit: number;
  currentBalance: number;
  availableCredit: number;
  creditStatus: string;
  utilization: number;
  onHold: boolean;
}

/**
 * Stat card with tactical styling
 */
function StatCard({
  label,
  value,
  subValue,
  icon,
  status = 'operational',
  linkTo,
}: {
  label: string;
  value: string | number;
  subValue?: string;
  icon: React.ReactNode;
  status?: 'operational' | 'warning' | 'critical';
  linkTo?: string;
}) {
  const statusColor = {
    operational: 'bg-success-green',
    warning: 'bg-warning-amber',
    critical: 'bg-error-red',
  }[status];

  const borderColor = {
    operational: 'border-steel',
    warning: 'border-warning-amber',
    critical: 'border-error-red',
  }[status];

  const Content = (
    <div className="relative stat-card h-full">
      <div className={`absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 ${borderColor}`} />
      <div className={`absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 ${borderColor}`} />
      <div className={`absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 ${borderColor}`} />
      <div className={`absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 ${borderColor}`} />

      <div className="relative bg-panel-dark border border-panel-border hover:border-amber/60 transition-all duration-300 p-4 h-full clip-path-octagon">
        <div className="absolute inset-0 shadow-[inset_0_2px_8px_rgba(0,0,0,0.6)] pointer-events-none" />

        <div className="absolute top-3 right-3">
          <div
            className={`w-2 h-2 ${statusColor} rounded-full animate-pulse`}
            style={{ boxShadow: '0 0 8px currentColor' }}
          />
        </div>

        <div className="flex items-center space-x-2 mb-2">
          <div className="text-steel">{icon}</div>
          <span className="text-[10px] uppercase tracking-[0.15em] text-text-secondary font-mono">
            {label}
          </span>
        </div>

        <div className="text-2xl font-mono font-bold text-amber" style={{ textShadow: '0 0 10px rgba(255,149,0,0.6)' }}>
          {value}
        </div>

        {subValue && (
          <div className="text-xs text-text-tertiary font-mono mt-1">{subValue}</div>
        )}

        {linkTo && (
          <div className="flex items-center text-xs text-amber font-mono mt-2 opacity-60 group-hover:opacity-100">
            View Details <ChevronRight className="w-3 h-3 ml-1" />
          </div>
        )}
      </div>
    </div>
  );

  if (linkTo) {
    return (
      <Link to={linkTo} className="block group">
        {Content}
      </Link>
    );
  }

  return Content;
}

/**
 * Customer row in holds table
 */
function HoldRow({
  hold,
  onRelease,
}: {
  hold: CustomerOnHold;
  onRelease: (holdId: string) => void;
}) {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const creditLimit = hold.customer.creditLimit ?? 0;
  const utilization = creditLimit > 0 ? (hold.customer.currentBalance / creditLimit) * 100 : 0;

  return (
    <tr className="border-b border-panel-border hover:bg-panel-border/20 transition-colors">
      <td className="px-4 py-3">
        <Link to={`/customers/${hold.customer.id}`} className="hover:text-amber transition-colors">
          <div className="font-mono text-sm text-text-primary">{hold.customer.customerNumber}</div>
          <div className="text-xs text-text-tertiary">{hold.customer.name}</div>
        </Link>
      </td>
      <td className="px-4 py-3">
        <span className="px-2 py-1 bg-error-red/10 text-error-red text-xs font-mono uppercase rounded">
          {hold.reason.replace('_', ' ')}
        </span>
      </td>
      <td className="px-4 py-3 text-right font-mono text-sm text-amber">
        {formatCurrency(hold.customer.currentBalance)}
      </td>
      <td className="px-4 py-3 text-right font-mono text-sm text-text-secondary">
        {creditLimit > 0 ? formatCurrency(creditLimit) : 'N/A'}
      </td>
      <td className="px-4 py-3">
        <div className="w-24">
          <div className="flex justify-between text-[10px] font-mono mb-1">
            <span className="text-text-tertiary">Utilization</span>
            <span className={utilization > 100 ? 'text-error-red' : utilization > 90 ? 'text-warning-amber' : 'text-success-green'}>
              {utilization.toFixed(0)}%
            </span>
          </div>
          <div className="h-1.5 bg-background rounded overflow-hidden">
            <div
              className={`h-full ${utilization > 100 ? 'bg-error-red' : utilization > 90 ? 'bg-warning-amber' : 'bg-success-green'}`}
              style={{ width: `${Math.min(utilization, 100)}%` }}
            />
          </div>
        </div>
      </td>
      <td className="px-4 py-3 text-xs font-mono text-text-tertiary">
        {formatDate(hold.holdDate)}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center space-x-2">
          <Link
            to={`/customers/${hold.customer.id}/credit`}
            className="p-1.5 text-steel hover:text-amber hover:bg-amber/10 rounded transition-colors"
            title="View Credit Details"
          >
            <CreditCard className="w-4 h-4" />
          </Link>
          <button
            onClick={() => onRelease(hold.id)}
            className="p-1.5 text-success-green hover:text-success-green hover:bg-success-green/10 rounded transition-colors"
            title="Release Hold"
          >
            <CheckCircle className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  );
}

/**
 * At-risk customer row
 */
function AtRiskRow({ customer }: { customer: CustomerAtRisk }) {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  return (
    <tr className="border-b border-panel-border hover:bg-panel-border/20 transition-colors">
      <td className="px-4 py-3">
        <Link to={`/customers/${customer.id}`} className="hover:text-amber transition-colors">
          <div className="font-mono text-sm text-text-primary">{customer.customerNumber}</div>
          <div className="text-xs text-text-tertiary">{customer.name}</div>
        </Link>
      </td>
      <td className="px-4 py-3">
        <span className={`px-2 py-1 text-xs font-mono uppercase rounded ${
          customer.creditStatus === 'HOLD'
            ? 'bg-error-red/10 text-error-red'
            : customer.creditStatus === 'WARNING'
              ? 'bg-warning-amber/10 text-warning-amber'
              : 'bg-success-green/10 text-success-green'
        }`}>
          {customer.creditStatus}
        </span>
      </td>
      <td className="px-4 py-3 text-right font-mono text-sm text-amber">
        {formatCurrency(customer.currentBalance)}
      </td>
      <td className="px-4 py-3 text-right font-mono text-sm text-text-secondary">
        {formatCurrency(customer.creditLimit)}
      </td>
      <td className="px-4 py-3 text-right font-mono text-sm text-success-green">
        {formatCurrency(customer.availableCredit)}
      </td>
      <td className="px-4 py-3">
        <div className="w-24">
          <div className="flex justify-between text-[10px] font-mono mb-1">
            <span className="text-text-tertiary">Util</span>
            <span className={customer.utilization > 100 ? 'text-error-red' : customer.utilization > 90 ? 'text-warning-amber' : 'text-success-green'}>
              {customer.utilization.toFixed(0)}%
            </span>
          </div>
          <div className="h-1.5 bg-background rounded overflow-hidden">
            <div
              className={`h-full ${customer.utilization > 100 ? 'bg-error-red' : customer.utilization > 90 ? 'bg-warning-amber' : 'bg-success-green'}`}
              style={{ width: `${Math.min(customer.utilization, 100)}%` }}
            />
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        <Link
          to={`/customers/${customer.id}/credit`}
          className="p-1.5 text-steel hover:text-amber hover:bg-amber/10 rounded transition-colors inline-block"
          title="Manage Credit"
        >
          <CreditCard className="w-4 h-4" />
        </Link>
      </td>
    </tr>
  );
}

/**
 * Credit Management Dashboard Page
 */
const CreditManagementPage = () => {
  const { accessToken } = useAuth();
  const [overview, setOverview] = useState<CreditOverview | null>(null);
  const [holds, setHolds] = useState<CustomerOnHold[]>([]);
  const [atRiskCustomers, setAtRiskCustomers] = useState<CustomerAtRisk[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'holds' | 'at-risk'>('holds');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchCreditData();
  }, []);

  const fetchCreditData = async () => {
    setIsLoading(true);
    try {
      const [overviewRes, holdsRes, atRiskRes] = await Promise.all([
        fetch(`${API_URL}/api/credit/overview`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        }),
        fetch(`${API_URL}/api/credit/holds`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        }),
        fetch(`${API_URL}/api/credit/at-risk`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        }),
      ]);

      if (overviewRes.ok) {
        const data = await overviewRes.json();
        setOverview(data.overview);
      }

      if (holdsRes.ok) {
        const data = await holdsRes.json();
        setHolds(data.holds || []);
      }

      if (atRiskRes.ok) {
        const data = await atRiskRes.json();
        setAtRiskCustomers(data.customers || []);
      }
    } catch (error) {
      console.error('Failed to fetch credit data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReleaseHold = async (holdId: string) => {
    if (!confirm('Are you sure you want to release this credit hold?')) return;

    try {
      const response = await fetch(`${API_URL}/api/credit/hold/${holdId}/release`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ notes: 'Released from Credit Management dashboard' }),
      });

      if (response.ok) {
        fetchCreditData();
      }
    } catch (error) {
      console.error('Failed to release hold:', error);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  // Filter holds by search
  const filteredHolds = holds.filter(
    (hold) =>
      hold.customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      hold.customer.customerNumber.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Filter at-risk customers by search
  const filteredAtRisk = atRiskCustomers.filter(
    (customer) =>
      customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.customerNumber.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-amber font-mono animate-pulse">Loading Credit Management...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative">
        <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber shadow-[0_0_8px_rgba(255,149,0,0.6)]" />
        <div className="border-l-4 border-amber pl-6">
          <div className="flex items-center justify-between">
            <div>
              <h1
                className="text-3xl font-display font-bold text-steel tracking-wide uppercase"
                style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
              >
                Credit Management
              </h1>
              <p className="text-text-secondary text-sm font-mono mt-1">
                Monitor customer credit, manage holds, and track AR exposure
              </p>
            </div>
            <button
              onClick={fetchCreditData}
              className="flex items-center space-x-2 px-4 py-2 bg-steel/10 hover:bg-steel/20 border border-steel/30 text-steel font-mono text-sm uppercase tracking-wider transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Refresh</span>
            </button>
          </div>
        </div>
      </div>

      {/* Overview Stats */}
      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <StatCard
            label="Total AR"
            value={formatCurrency(overview.totalAR)}
            icon={<DollarSign className="w-4 h-4" />}
            status="operational"
          />
          <StatCard
            label="Credit Extended"
            value={formatCurrency(overview.totalCreditExtended)}
            icon={<TrendingUp className="w-4 h-4" />}
            status="operational"
          />
          <StatCard
            label="Active Customers"
            value={overview.totalCustomers}
            subValue={`${overview.customersWithCredit} with credit`}
            icon={<Users className="w-4 h-4" />}
            status="operational"
          />
          <StatCard
            label="On Hold"
            value={overview.customersOnHold}
            icon={<ShieldAlert className="w-4 h-4" />}
            status={overview.customersOnHold > 0 ? 'critical' : 'operational'}
          />
          <StatCard
            label="Warning"
            value={overview.customersWarning}
            icon={<AlertTriangle className="w-4 h-4" />}
            status={overview.customersWarning > 0 ? 'warning' : 'operational'}
          />
          <StatCard
            label="Good Standing"
            value={overview.totalCustomers - overview.customersOnHold - overview.customersWarning}
            icon={<ShieldCheck className="w-4 h-4" />}
            status="operational"
          />
        </div>
      )}

      {/* Tabs and Search */}
      <div className="flex items-center justify-between">
        <div className="flex space-x-1 bg-panel-dark border border-panel-border p-1">
          <button
            onClick={() => setActiveTab('holds')}
            className={`px-4 py-2 font-mono text-xs uppercase tracking-wider transition-colors ${
              activeTab === 'holds'
                ? 'bg-amber text-background'
                : 'text-text-secondary hover:text-amber'
            }`}
          >
            <span className="flex items-center space-x-2">
              <ShieldAlert className="w-4 h-4" />
              <span>On Hold ({holds.length})</span>
            </span>
          </button>
          <button
            onClick={() => setActiveTab('at-risk')}
            className={`px-4 py-2 font-mono text-xs uppercase tracking-wider transition-colors ${
              activeTab === 'at-risk'
                ? 'bg-warning-amber text-background'
                : 'text-text-secondary hover:text-warning-amber'
            }`}
          >
            <span className="flex items-center space-x-2">
              <AlertTriangle className="w-4 h-4" />
              <span>At Risk ({atRiskCustomers.length})</span>
            </span>
          </button>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary" />
          <input
            type="text"
            placeholder="Search customers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 bg-panel-dark border border-panel-border text-text-primary font-mono text-sm placeholder-text-tertiary focus:border-amber focus:outline-none w-64"
          />
        </div>
      </div>

      {/* Data Table */}
      <div className="relative stat-card">
        <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
        <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
        <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
        <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

        <div className="bg-panel-dark border border-panel-border overflow-hidden">
          {activeTab === 'holds' ? (
            <table className="w-full">
              <thead className="bg-background/50 border-b border-panel-border">
                <tr>
                  <th className="px-4 py-3 text-left text-[10px] font-mono uppercase tracking-wider text-text-tertiary">
                    Customer
                  </th>
                  <th className="px-4 py-3 text-left text-[10px] font-mono uppercase tracking-wider text-text-tertiary">
                    Reason
                  </th>
                  <th className="px-4 py-3 text-right text-[10px] font-mono uppercase tracking-wider text-text-tertiary">
                    Balance
                  </th>
                  <th className="px-4 py-3 text-right text-[10px] font-mono uppercase tracking-wider text-text-tertiary">
                    Credit Limit
                  </th>
                  <th className="px-4 py-3 text-left text-[10px] font-mono uppercase tracking-wider text-text-tertiary">
                    Utilization
                  </th>
                  <th className="px-4 py-3 text-left text-[10px] font-mono uppercase tracking-wider text-text-tertiary">
                    Hold Date
                  </th>
                  <th className="px-4 py-3 text-left text-[10px] font-mono uppercase tracking-wider text-text-tertiary">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredHolds.length > 0 ? (
                  filteredHolds.map((hold) => (
                    <HoldRow key={hold.id} hold={hold} onRelease={handleReleaseHold} />
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center">
                      <div className="flex flex-col items-center space-y-2">
                        <ShieldCheck className="w-8 h-8 text-success-green" />
                        <span className="text-text-secondary font-mono text-sm">
                          No customers on credit hold
                        </span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          ) : (
            <table className="w-full">
              <thead className="bg-background/50 border-b border-panel-border">
                <tr>
                  <th className="px-4 py-3 text-left text-[10px] font-mono uppercase tracking-wider text-text-tertiary">
                    Customer
                  </th>
                  <th className="px-4 py-3 text-left text-[10px] font-mono uppercase tracking-wider text-text-tertiary">
                    Status
                  </th>
                  <th className="px-4 py-3 text-right text-[10px] font-mono uppercase tracking-wider text-text-tertiary">
                    Balance
                  </th>
                  <th className="px-4 py-3 text-right text-[10px] font-mono uppercase tracking-wider text-text-tertiary">
                    Credit Limit
                  </th>
                  <th className="px-4 py-3 text-right text-[10px] font-mono uppercase tracking-wider text-text-tertiary">
                    Available
                  </th>
                  <th className="px-4 py-3 text-left text-[10px] font-mono uppercase tracking-wider text-text-tertiary">
                    Utilization
                  </th>
                  <th className="px-4 py-3 text-left text-[10px] font-mono uppercase tracking-wider text-text-tertiary">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredAtRisk.length > 0 ? (
                  filteredAtRisk.map((customer) => (
                    <AtRiskRow key={customer.id} customer={customer} />
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center">
                      <div className="flex flex-col items-center space-y-2">
                        <ShieldCheck className="w-8 h-8 text-success-green" />
                        <span className="text-text-secondary font-mono text-sm">
                          No customers at risk
                        </span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Recent Hold Activity */}
      {overview && overview.recentHolds.length > 0 && (
        <div className="relative stat-card">
          <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-steel" />
          <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-steel" />
          <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-steel" />
          <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-steel" />

          <div className="bg-panel-dark border border-panel-border p-4">
            <div className="flex items-center space-x-2 mb-4 pb-2 border-b border-panel-border">
              <Clock className="w-4 h-4 text-steel" />
              <h3 className="text-sm font-display text-steel uppercase tracking-wide">
                Recent Hold Activity
              </h3>
            </div>

            <div className="space-y-2">
              {overview.recentHolds.map((hold) => (
                <div
                  key={hold.id}
                  className="flex items-center justify-between py-2 border-b border-panel-border/50 last:border-0"
                >
                  <div className="flex items-center space-x-3">
                    {hold.isActive ? (
                      <XCircle className="w-4 h-4 text-error-red" />
                    ) : (
                      <CheckCircle className="w-4 h-4 text-success-green" />
                    )}
                    <div>
                      <Link
                        to={`/customers/${hold.customer.id}`}
                        className="text-sm font-mono text-text-primary hover:text-amber transition-colors"
                      >
                        {hold.customer.name}
                      </Link>
                      <div className="text-xs text-text-tertiary">
                        {hold.reason.replace('_', ' ')} - {hold.customer.customerNumber}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`text-xs font-mono ${hold.isActive ? 'text-error-red' : 'text-success-green'}`}>
                      {hold.isActive ? 'Active' : 'Released'}
                    </div>
                    <div className="text-[10px] text-text-tertiary font-mono">
                      {new Date(hold.holdDate).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreditManagementPage;
